import React, { ReactNode } from "react";

import { faXmark } from "@fortawesome/pro-solid-svg-icons/faXmark";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip, { TooltipProps } from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import { Button, ButtonProps } from "@mapmycustomers/ui";

import IWizardStep from "@app/component/wizard/types/IWizardStep";

import commonStyles from "./CommonStepStyles.module.scss";
import styles from "./StandardStep.module.scss";

export interface StandardStepProps extends Pick<
  IWizardStep<any>,
  "onCancel" | "onGoBack" | "onGoForward"
> {
  backButtonDisabled?: boolean;
  backButtonHidden?: boolean;
  backButtonProps?: Omit<ButtonProps, "children" | "disabled" | "hidden" | "onClick">;
  backButtonText?: ReactNode;
  cancelButtonDisabled?: boolean;
  cancelButtonHidden?: boolean;
  cancelButtonProps?: Omit<ButtonProps, "children" | "disabled" | "hidden" | "onClick">;
  cancelButtonText?: ReactNode;
  canClose?: boolean;
  children: ReactNode;
  className?: string;
  containerClassName?: string;
  footer?: ReactNode;
  nextButtonDisabled?: boolean;
  nextButtonHidden?: boolean;
  nextButtonProps?: Omit<ButtonProps, "children" | "disabled" | "hidden" | "onClick">;
  nextButtonText?: ReactNode;
  nextButtonTooltipProps?: TooltipProps;
  showHeader?: boolean;
  title?: ReactNode;
}

const StandardStep = ({
  backButtonDisabled,
  backButtonHidden,
  backButtonProps,
  backButtonText,
  cancelButtonDisabled,
  cancelButtonHidden = true,
  cancelButtonProps,
  cancelButtonText,
  canClose = true,
  children,
  className,
  containerClassName,
  footer,
  nextButtonDisabled,
  nextButtonHidden,
  nextButtonProps,
  nextButtonText,
  nextButtonTooltipProps,
  onCancel,
  onGoBack,
  onGoForward,
  showHeader = true,
  title,
}: StandardStepProps) => {
  const intl = useIntl();

  return (
    <div className={cn(styles.container, containerClassName)}>
      {showHeader && (
        <div className={commonStyles.header}>
          <div className={commonStyles.title}>{title}</div>
          {canClose && (
            <div className={commonStyles.closeIconWrapper}>
              <FontAwesomeIcon
                className={commonStyles.closeIcon}
                icon={faXmark}
                onClick={onCancel}
              />
            </div>
          )}
        </div>
      )}
      <div className={cn(commonStyles.content, className)}>{children}</div>
      {footer === undefined ? (
        <div className={commonStyles.footer}>
          <div>
            <Button
              disabled={backButtonDisabled}
              hidden={backButtonHidden}
              onClick={onGoBack}
              {...backButtonProps}
            >
              {backButtonText ||
                intl.formatMessage({
                  id: "wizard.standardStep.footer.back",
                  defaultMessage: "Go Back",
                  description: "Back button title in a StandardStep of a Wizard component",
                })}
            </Button>
          </div>
          <div className={styles.rightButtons}>
            <Button
              disabled={cancelButtonDisabled}
              hidden={cancelButtonHidden}
              onClick={onCancel}
              type="default"
              {...cancelButtonProps}
            >
              {cancelButtonText ||
                intl.formatMessage({
                  id: "wizard.standardStep.footer.cancel",
                  defaultMessage: "Cancel",
                  description: "Cancel button title in a StandardStep of a Wizard component",
                })}
            </Button>
            <Tooltip {...nextButtonTooltipProps}>
              <span>
                <Button
                  disabled={nextButtonDisabled}
                  hidden={nextButtonHidden}
                  onClick={onGoForward}
                  type="primary"
                  {...nextButtonProps}
                >
                  {nextButtonText ||
                    intl.formatMessage({
                      id: "wizard.standardStep.footer.next",
                      defaultMessage: "Next",
                      description: "Next button title in a StandardStep of a Wizard component",
                    })}
                </Button>
              </span>
            </Tooltip>
          </div>
        </div>
      ) : (
        footer
      )}
    </div>
  );
};

export default StandardStep;
